import { addBusiness, addCredential, revokeCredential } from './identity';
import { addTenant, connectTenant, ConsoleState, grantVendorGroups, joinTenant } from './store';

/**
 * Taking chosen tenants out of the directory and keeping them here.
 *
 * The directory is the system that already owns tenants, businesses and the key each business
 * was given. Reading it on every request would put someone else's uptime on our request path,
 * so the pieces we need are copied here once, when a person picks them, and the request path
 * then answers "who is calling" from memory alone.
 *
 * Only three things are copied: the id, the name, and the *hash* of the key. The key itself is
 * hashed where the directory is read — on the server — and the plaintext is discarded there, so
 * it never reaches a browser, this document, or a backup of it.
 */

/** One business as the directory describes it. `apiKeyHash` is absent when it has no key yet. */
export interface DirectoryBusinessPick {
  id: string;
  name: string;
  apiKeyHash?: string;
  pos?: string;
}

/**
 * One tenant as the directory describes it, with the businesses chosen underneath it.
 * `apiKeyHash` is a key issued to the whole tenant, which opens any business under it.
 */
export interface DirectoryTenantPick {
  id: string;
  name: string;
  businesses?: DirectoryBusinessPick[];
  apiKeyHash?: string;
  details?: Record<string, string>;
}

const HASH = /^[a-f0-9]{64}$/;

/**
 * A business, and the key it is holding right now.
 *
 * Re-importing is how a rotated key is picked up: the new hash is added and the key this import
 * previously issued for that business is revoked, so the old key stops opening the door here at
 * the same moment it stopped working where it is owned. A key somebody created by hand is left
 * alone — it was not ours to withdraw.
 */
const importBusiness = (
  state: ConsoleState,
  tenantId: string,
  pick: DirectoryBusinessPick,
  now: string,
): ConsoleState => {
  const id = pick.id?.trim();
  const name = pick.name?.trim();
  if (!id || !name) return state;

  const pos = pick.pos?.trim() || undefined;
  const known = state.businesses?.some((business) => business.id === id);
  const added = known ? state : addBusiness(state, tenantId, name, id);
  let next = {
    ...added,
    businesses: (added.businesses ?? []).map((business) =>
      business.id === id ? { ...business, name, tenantId, ...(pos ? { pos } : {}) } : business,
    ),
  };

  const keyHash = pick.apiKeyHash?.trim().toLowerCase();
  if (!keyHash || !HASH.test(keyHash)) return next;
  /*
   * A key already on file is left exactly as it is — whether it is this business's live key,
   * or one that was withdrawn. Withdrawing the current key and then failing to re-add a hash
   * that is already spoken for would leave the business with no way in at all.
   */
  if (next.credentials?.some((credential) => credential.keyHash === keyHash)) return next;

  next = (next.credentials ?? [])
    .filter((credential) => credential.businessId === id && credential.origin === 'directory' && !credential.revokedAt)
    .reduce((held, credential) => revokeCredential(held, credential.id, now), next);

  return addCredential(next, {
    keyHash,
    businessId: id,
    label: `${name} — from the directory`,
    origin: 'directory',
    createdAt: now,
  });
};

/**
 * The key a tenant holds for all of its businesses, under the same rules as a business's own key.
 *
 * It is stored after the businesses, because a tenant key needs a business under that tenant to
 * belong to; a tenant with none brought in has nothing for the key to open.
 */
const importTenantKey = (
  state: ConsoleState,
  tenantId: string,
  name: string,
  hash: string | undefined,
  now: string,
) => {
  const keyHash = hash?.trim().toLowerCase();
  if (!keyHash || !HASH.test(keyHash)) return state;
  if (state.credentials?.some((credential) => credential.keyHash === keyHash)) return state;
  if (!state.businesses?.some((business) => business.tenantId === tenantId)) return state;

  const next = (state.credentials ?? [])
    .filter(
      (credential) =>
        !credential.businessId &&
        credential.tenantId === tenantId &&
        credential.origin === 'directory' &&
        !credential.revokedAt,
    )
    .reduce((held, credential) => revokeCredential(held, credential.id, now), state);

  return addCredential(next, {
    keyHash,
    businessId: '',
    tenantId,
    label: `${name} — tenant key from the directory`,
    origin: 'directory',
    createdAt: now,
  });
};

/**
 * Copy the chosen tenants in, and connect each to the vendor being configured.
 *
 * Running it twice is the same as running it once: names are refreshed, nothing is duplicated,
 * and a tenant already connected to the vendor stays connected. That matters because this is
 * how the directory is re-read, not only how it is first read.
 */
export const importDirectory = (
  state: ConsoleState,
  picks: DirectoryTenantPick[],
  vendorId?: string,
  now: string = new Date().toISOString(),
  grant = true,
): ConsoleState =>
  picks.reduce((carried, pick) => {
    const id = pick.id?.trim();
    const name = pick.name?.trim();
    if (!id || !name) return carried;

    let next = carried.tenants.some((tenant) => tenant.id === id)
      ? { ...carried, tenants: carried.tenants.map((tenant) => (tenant.id === id ? { ...tenant, name } : tenant)) }
      : addTenant(carried, name, id);
    if (!next.tenants.some((tenant) => tenant.id === id)) return carried;
    if (pick.details) {
      const details = pick.details;
      next = { ...next, tenants: next.tenants.map((tenant) => (tenant.id === id ? { ...tenant, details } : tenant)) };
    }

    if (vendorId) next = grant ? connectTenant(next, id, vendorId) : joinTenant(next, id, vendorId);

    next = (pick.businesses ?? []).reduce((held, business) => importBusiness(held, id, business, now), next);
    /* branches that arrive after their shop was connected need its vendors' groups as well */
    if (grant)
      next = (next.tenants.find((tenant) => tenant.id === id)?.vendorIds ?? []).reduce(
        (held, connected) => grantVendorGroups(held, connected, id),
        next,
      );

    return importTenantKey(next, id, name, pick.apiKeyHash, now);
  }, state);
